import React from 'react';

interface UserAvatarProps {
  photoUrl?: string | null;
  fullName: string;
  size?: 'sm' | 'md' | 'lg' | 'xl';
  colorIndex?: number;
  className?: string;
  showOnlineIndicator?: boolean;
  isOnline?: boolean;
}

const AVATAR_COLORS = [
  'bg-blue-500',
  'bg-emerald-500',
  'bg-purple-500',
  'bg-orange-500',
  'bg-pink-500',
  'bg-indigo-500',
  'bg-teal-500',
  'bg-rose-500',
];

const SIZE_CLASSES = {
  sm: 'w-8 h-8 text-xs',
  md: 'w-10 h-10 text-sm',
  lg: 'w-12 h-12 text-base',
  xl: 'w-16 h-16 text-lg',
};

/**
 * Get initials from a name (e.g., "Dr. John Smith" -> "JS")
 */
const getInitials = (name?: string): string => {
  if (!name) return '?';
  
  // Remove common prefixes like "Dr.", "Dr"
  const cleanName = name.replace(/^(Dr\.?\s*)/i, '').trim();
  
  const parts = cleanName.split(' ').filter(p => p.length > 0);
  if (parts.length === 0) return '?';
  if (parts.length === 1) return parts[0].charAt(0).toUpperCase();
  
  // Get first letter of first and last name
  return (parts[0].charAt(0) + parts[parts.length - 1].charAt(0)).toUpperCase();
};

/**
 * Get a consistent color for a name (based on hash)
 */
const getColorForName = (name: string): string => {
  let hash = 0;
  for (let i = 0; i < name.length; i++) {
    hash = name.charCodeAt(i) + ((hash << 5) - hash);
  }
  return AVATAR_COLORS[Math.abs(hash) % AVATAR_COLORS.length];
};

export const UserAvatar: React.FC<UserAvatarProps> = ({
  photoUrl,
  fullName,
  size = 'md',
  colorIndex,
  className = '',
  showOnlineIndicator = false,
  isOnline = false,
}) => {
  const sizeClass = SIZE_CLASSES[size];
  const bgColor = colorIndex !== undefined 
    ? AVATAR_COLORS[colorIndex % AVATAR_COLORS.length]
    : getColorForName(fullName);
  const initials = getInitials(fullName);

  return (
    <div className={`relative ${className}`}>
      <div
        className={`
          ${sizeClass}
          rounded-full flex items-center justify-center
          font-semibold text-white overflow-hidden
          ${!photoUrl ? bgColor : ''}
        `}
      >
        {photoUrl ? (
          <img
            src={photoUrl}
            alt={fullName}
            className="block w-full h-full object-cover"
            onError={(e) => {
              // Fallback to initials if image fails to load
              e.currentTarget.style.display = 'none';
              e.currentTarget.parentElement?.classList.add(bgColor);
              const initialsSpan = document.createElement('span');
              initialsSpan.textContent = initials;
              e.currentTarget.parentElement?.appendChild(initialsSpan);
            }}
          />
        ) : (
          <span>{initials}</span>
        )}
      </div>
      {showOnlineIndicator && isOnline && (
        <span className="absolute bottom-0 right-0 block w-3 h-3 bg-green-500 rounded-full ring-2 ring-white dark:ring-dark-bg-secondary" />
      )}
    </div>
  );
};

export default UserAvatar;

